import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";

const APPLY_AT_IMMEDIATE = "immediate";
const APPLY_AT_NEXT_CYCLE = "next_cycle";

/**
 * Resolve the pricing block for a given apply-at mode. Falls back to the
 * immediate block when the next-cycle mode is unavailable (pre-collection
 * orders), so the dialog never crashes if the option is missing.
 *
 * @param {object|null} priceDifference   Server-provided dual-mode pricing.
 * @param {string} mode                   "immediate" or "next_cycle".
 * @returns {object} The pricing entry with current_cycle / next_cycle / schedule_only.
 */
const resolvePricing = (priceDifference, mode) => {
  if (!priceDifference) {
    return { current_cycle: 0, next_cycle: 0, schedule_only: false };
  }
  if (priceDifference[mode]) return priceDifference[mode];
  return priceDifference[APPLY_AT_IMMEDIATE] || {
    current_cycle: 0,
    next_cycle: 0,
    schedule_only: false,
  };
};

/**
 * Confirmation modal for switching the access type on a DUSS order. Lets the
 * agent pick whether the change applies immediately or from the next billing
 * cycle, and shows the pricing impact for the chosen mode.
 *
 * @param {object}   props
 * @param {boolean}  props.show              Whether the modal is visible.
 * @param {function} props.onCancel          Cancel handler.
 * @param {function} props.onConfirm         Confirm handler. Receives the chosen apply_at.
 * @param {boolean}  props.submitting        Whether the PUT request is in flight.
 * @param {string}   props.fromLabel         Current access label.
 * @param {string}   props.toLabel           Target access label.
 * @param {object}   props.priceDifference   Dual-mode pricing { immediate, next_cycle? }.
 * @param {string}   props.currencySymbol    e.g. "£".
 * @param {string}   [props.periodUnit]      e.g. "month".
 * @returns {JSX.Element}
 */
const ChangeAccessTypeDialog = ({
  show,
  onCancel,
  onConfirm,
  submitting,
  fromLabel,
  toLabel,
  priceDifference,
  currencySymbol,
  periodUnit,
}) => {
  const nextCycleAvailable = Boolean(priceDifference?.[APPLY_AT_NEXT_CYCLE]);
  const [applyAt, setApplyAt] = useState(APPLY_AT_IMMEDIATE);

  useEffect(() => {
    if (show) setApplyAt(APPLY_AT_IMMEDIATE);
  }, [show]);

  const pricing = useMemo(() => resolvePricing(priceDifference, applyAt), [
    priceDifference,
    applyAt,
  ]);

  const currentCycle = Number(pricing?.current_cycle ?? 0);
  const nextCycle = Number(pricing?.next_cycle ?? 0);
  const scheduleOnly = Boolean(pricing?.schedule_only);
  const cycleLabel = periodUnit ? `/${periodUnit}` : "";
  const nextCycleAbs = Math.abs(nextCycle).toFixed(2);
  const direction =
    nextCycle > 0 ? "increase" : nextCycle < 0 ? "decrease" : "no change";
  const isImmediate = applyAt === APPLY_AT_IMMEDIATE;

  return (
    <Modal show={show} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>Confirm access type change</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Switching from <strong>{fromLabel}</strong> to <strong>{toLabel}</strong>.
        </p>

        <div className="mb-4">
          <Form.Check
            type="radio"
            id="apply-at-immediate"
            name="apply-at"
            label="Apply now"
            checked={isImmediate}
            onChange={() => setApplyAt(APPLY_AT_IMMEDIATE)}
            disabled={submitting}
          />
          {nextCycleAvailable && (
            <Form.Check
              type="radio"
              id="apply-at-next-cycle"
              name="apply-at"
              label="Apply from next billing cycle"
              checked={applyAt === APPLY_AT_NEXT_CYCLE}
              onChange={() => setApplyAt(APPLY_AT_NEXT_CYCLE)}
              disabled={submitting}
            />
          )}
        </div>

        {scheduleOnly ? (
          <div className="alert alert-light">
            <p className="mb-1">
              <strong>No charge today.</strong>
            </p>
            <p className="mb-0">
              {isImmediate
                ? `Customer will switch to ${toLabel} immediately. Stripe will bill ${currencySymbol}${nextCycleAbs}${cycleLabel} ${direction} from next renewal.`
                : `Customer keeps ${fromLabel} until next billing cycle, then switches to ${toLabel} (${currencySymbol}${nextCycleAbs}${cycleLabel} ${direction}).`}
            </p>
          </div>
        ) : (
          <div className="alert alert-light">
            <p className="mb-1">
              <strong>Today's charge:</strong> {currencySymbol}
              {currentCycle.toFixed(2)}
            </p>
            <p className="mb-0">
              From next billing cycle: {currencySymbol}
              {nextCycleAbs}
              {cycleLabel} {direction}.
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-light btn-elevate"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary font-weight-bolder font-size-sm"
          onClick={() => onConfirm(applyAt)}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" />
              Applying...
            </>
          ) : (
            "Confirm change"
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeAccessTypeDialog;
